import React, { createContext, useState, useEffect } from 'react';
import { getCurrentUser } from './auth';

// Context to store search, filters, filterLogic, and sort configuration
export const FilterSortContext = createContext();

// Function to get user-specific storage key
const getUserStorageKey = (baseKey) => {
  const user = getCurrentUser();
  const userEmail = user ? user.email : 'anonymous';
  return `${userEmail}_${baseKey}`;
};

// Local storage keys
const STORAGE_KEYS = {
  SEARCH_QUERY: 'napco_search_query',
  FILTERS: 'napco_filters',
  FILTER_LOGIC: 'napco_filter_logic',
  SORT_CONFIGS: 'napco_sort_configs',
  PAGE_SIZE: 'napco_page_size'
};

export function FilterSortProvider({ children }) {
  // State to track the current user
  const [currentUser, setCurrentUser] = useState(getCurrentUser());

  // Update current user when auth state changes
  useEffect(() => {
    const checkUser = () => {
      setCurrentUser(getCurrentUser());
    };
    
    // Check immediately and then set up an interval to check periodically
    checkUser();
    const interval = setInterval(checkUser, 5000); // Check every 5 seconds
    
    return () => clearInterval(interval);
  }, []);

  // Initialize state from localStorage or default values
  const [searchQuery, setSearchQuery] = useState(() => {
    const saved = localStorage.getItem(getUserStorageKey(STORAGE_KEYS.SEARCH_QUERY));
    return saved ? JSON.parse(saved) : '';
  });

  const [filters, setFilters] = useState(() => {
    const saved = localStorage.getItem(getUserStorageKey(STORAGE_KEYS.FILTERS));
    return saved ? JSON.parse(saved) : [];
  });

  const [filterLogic, setFilterLogic] = useState(() => {
    const saved = localStorage.getItem(getUserStorageKey(STORAGE_KEYS.FILTER_LOGIC));
    return saved ? JSON.parse(saved) : 'AND';
  });

  const [sortConfigs, setSortConfigs] = useState(() => {
    const saved = localStorage.getItem(getUserStorageKey(STORAGE_KEYS.SORT_CONFIGS));
    return saved ? JSON.parse(saved) : [];
  });

  const [pageSize, setPageSize] = useState(() => {
    const saved = localStorage.getItem(getUserStorageKey(STORAGE_KEYS.PAGE_SIZE));
    return saved ? JSON.parse(saved) : 50;
  });

  // Reload state when user changes
  useEffect(() => {
    // Reload all state from localStorage with the new user-specific keys
    const savedSearchQuery = localStorage.getItem(getUserStorageKey(STORAGE_KEYS.SEARCH_QUERY));
    setSearchQuery(savedSearchQuery ? JSON.parse(savedSearchQuery) : '');
    
    const savedFilters = localStorage.getItem(getUserStorageKey(STORAGE_KEYS.FILTERS));
    setFilters(savedFilters ? JSON.parse(savedFilters) : []);
    
    const savedFilterLogic = localStorage.getItem(getUserStorageKey(STORAGE_KEYS.FILTER_LOGIC));
    setFilterLogic(savedFilterLogic ? JSON.parse(savedFilterLogic) : 'AND');
    
    const savedSortConfigs = localStorage.getItem(getUserStorageKey(STORAGE_KEYS.SORT_CONFIGS));
    setSortConfigs(savedSortConfigs ? JSON.parse(savedSortConfigs) : []);
    
    const savedPageSize = localStorage.getItem(getUserStorageKey(STORAGE_KEYS.PAGE_SIZE));
    setPageSize(savedPageSize ? JSON.parse(savedPageSize) : 50);
  }, [currentUser]);

  // Update localStorage when state changes
  useEffect(() => {
    localStorage.setItem(getUserStorageKey(STORAGE_KEYS.SEARCH_QUERY), JSON.stringify(searchQuery));
  }, [searchQuery, currentUser]);

  useEffect(() => {
    localStorage.setItem(getUserStorageKey(STORAGE_KEYS.FILTERS), JSON.stringify(filters));
  }, [filters, currentUser]);

  useEffect(() => {
    localStorage.setItem(getUserStorageKey(STORAGE_KEYS.FILTER_LOGIC), JSON.stringify(filterLogic));
  }, [filterLogic, currentUser]);

  useEffect(() => {
    localStorage.setItem(getUserStorageKey(STORAGE_KEYS.SORT_CONFIGS), JSON.stringify(sortConfigs));
  }, [sortConfigs, currentUser]);

  useEffect(() => {
    localStorage.setItem(getUserStorageKey(STORAGE_KEYS.PAGE_SIZE), JSON.stringify(pageSize));
  }, [pageSize, currentUser]);

  // Function to reset all filters and sorting
  const resetAll = () => {
    setSearchQuery('');
    setFilters([]);
    setFilterLogic('AND');
    setSortConfigs([]);
    // Don't reset page size as that's a user preference
  };

  return (
    <FilterSortContext.Provider
      value={{
        searchQuery,
        setSearchQuery,
        filters,
        setFilters,
        filterLogic,
        setFilterLogic,
        sortConfigs,
        setSortConfigs,
        pageSize,
        setPageSize,
        resetAll,
      }}
    >
      {children}
    </FilterSortContext.Provider>
  );
}
